import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { clientAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Check, X, Download, FileText, MapPin, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const statusColors = {
  draft: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  sent: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  accepted: 'bg-green-500/20 text-green-400 border-green-500/30',
  rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
  expired: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
};

export const ClientPortalPage = () => {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchQuote();
  }, [token]);

  const fetchQuote = async () => {
    try {
      const response = await clientAPI.getQuote(token);
      setQuote(response.data.quote);
      setJob(response.data.job);
    } catch (error) {
      setError('Quote not found or link has expired');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    setProcessing(true);
    try {
      await clientAPI.approveQuote(token);
      toast.success('Quote approved! We will be in touch shortly.');
      fetchQuote();
    } catch (error) {
      toast.error('Failed to approve quote');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    setProcessing(true);
    try {
      await clientAPI.rejectQuote(token, rejectReason);
      toast.success('Quote response submitted');
      fetchQuote();
      setShowRejectForm(false);
    } catch (error) {
      toast.error('Failed to submit response');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await clientAPI.downloadPdf(token);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Quote-${quote?.quote_number}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md bg-card border-border rounded-lg">
          <CardContent className="p-8 text-center">
            <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Quote Not Found</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const canRespond = quote?.status === 'sent' || quote?.status === 'draft';

  return (
    <div className="min-h-screen bg-background p-4 md:p-8" data-testid="client-portal">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_ezclaimwin/artifacts/aasxwc8a_ChatGPT_Image_Jan_4__2026__09_48_44_AM-removebg-preview.png" 
            alt="My Project Agent" 
            className="h-24 object-contain mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold">Quote Review</h1>
          <p className="text-muted-foreground">Review and respond to your quote</p>
        </div>

        {/* Quote Card */}
        <Card className="bg-card border-border rounded-lg">
          <CardHeader className="border-b border-border">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle className="text-xl">{quote?.quote_number}</CardTitle>
                <CardDescription>Created: {new Date(quote?.created_at).toLocaleDateString()}</CardDescription>
              </div>
              <Badge className={`${statusColors[quote?.status]} rounded-lg uppercase font-mono`}>
                {quote?.status}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* Job Details */}
            {job && (
              <div className="p-4 bg-secondary/30 rounded-lg space-y-2">
                <h3 className="font-bold">{job.title}</h3>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  {job.address}
                </div>
                {job.description && (
                  <p className="text-sm text-muted-foreground">{job.description}</p>
                )}
              </div>
            )}

            {/* Client Info */}
            <div>
              <p className="text-sm text-muted-foreground">Prepared for:</p>
              <p className="font-bold">{quote?.client_name}</p>
              {quote?.valid_until && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                  <Calendar className="h-4 w-4" />
                  Valid until: {new Date(quote.valid_until).toLocaleDateString()}
                </div>
              )}
            </div>

            {/* Items Table */}
            <Table>
              <TableHeader>
                <TableRow className="border-border">
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Unit Price</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quote?.items?.map((item, index) => (
                  <TableRow key={index} className="border-border">
                    <TableCell>{item.description}</TableCell>
                    <TableCell className="text-right">{item.quantity}</TableCell>
                    <TableCell className="text-right font-mono">${item.unit_price?.toFixed(2)}</TableCell>
                    <TableCell className="text-right font-mono">${(item.quantity * item.unit_price)?.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Totals */}
            <div className="border-t border-border pt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span>Subtotal</span>
                <span className="font-mono">${quote?.subtotal?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Tax (10%)</span>
                <span className="font-mono">${quote?.tax?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold border-t border-border pt-2">
                <span>Total</span>
                <span className="font-mono text-primary">${quote?.total?.toFixed(2)}</span>
              </div>
            </div>

            {/* Notes */}
            {quote?.notes && (
              <div className="p-4 bg-secondary/30 rounded-lg">
                <p className="text-sm font-medium mb-1">Notes:</p>
                <p className="text-sm text-muted-foreground">{quote.notes}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button variant="outline" onClick={handleDownloadPdf} className="rounded-lg">
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
              
              {canRespond && !showRejectForm && (
                <>
                  <Button onClick={handleApprove} disabled={processing} className="bg-green-600 hover:bg-green-700 rounded-lg flex-1">
                    <Check className="h-4 w-4 mr-2" />
                    {processing ? 'Processing...' : 'Approve Quote'}
                  </Button>
                  <Button variant="destructive" onClick={() => setShowRejectForm(true)} className="rounded-lg flex-1">
                    <X className="h-4 w-4 mr-2" />
                    Decline
                  </Button>
                </>
              )}
            </div>

            {/* Reject Form */}
            {showRejectForm && (
              <div className="p-4 border border-border rounded-lg space-y-4">
                <p className="font-medium">Please let us know why you're declining:</p>
                <Textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Optional feedback..."
                  className="bg-input border-border rounded-lg"
                  rows={3}
                />
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowRejectForm(false)} className="rounded-lg">
                    Cancel
                  </Button>
                  <Button variant="destructive" onClick={handleReject} disabled={processing} className="rounded-lg">
                    {processing ? 'Submitting...' : 'Submit Response'}
                  </Button>
                </div>
              </div>
            )}

            {/* Status Messages */}
            {quote?.status === 'accepted' && (
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-center">
                <Check className="h-8 w-8 text-green-400 mx-auto mb-2" />
                <p className="font-bold text-green-400">Quote Approved</p>
                <p className="text-sm text-muted-foreground">Thank you! We will be in touch to schedule the work.</p>
              </div>
            )}

            {quote?.status === 'rejected' && (
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-center">
                <X className="h-8 w-8 text-red-400 mx-auto mb-2" />
                <p className="font-bold text-red-400">Quote Declined</p>
                <p className="text-sm text-muted-foreground">Thank you for your response.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-sm text-muted-foreground">
          Powered by My Project Agent
        </p>
      </div>
    </div>
  );
};

export default ClientPortalPage;
